import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ServiceBillingInfoService } from '../../services/service-billing-info.service';
import { SERVICE_BILLING_START_MS } from '../../../core/whatsapp/service-billing.generated';

/** localStorage key recording that the notice has been dismissed. */
const DISMISS_KEY = 'service_billing_notice_dismissed';

/**
 * How long after the cutover the notice keeps showing.
 *
 * The banner retires itself rather than needing a follow-up deploy to remove it.
 * Thirty days is long enough that someone returning after a quiet month still
 * finds out why their bill changed.
 */
const GRACE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Tells organisations that Meta starts charging for support replies.
 *
 * Dismissal is stored in localStorage, matching ConsentService. That is
 * per-browser rather than per-user, so someone on a second device sees it again
 * — acceptable for a one-time notice, and better than the alternative of never
 * showing it to someone who only ever uses their phone.
 */
@Component({
    selector: 'app-service-billing-banner',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="service-billing-banner" *ngIf="visible">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2">
                <div class="d-flex align-items-center">
                    <i class="bx bx-info-circle text-primary font-size-18 me-2"></i>
                    <span class="font-size-13">
                        <ng-container *ngIf="!hasStarted">
                            <strong>WhatsApp pricing changes on {{ startsAt | date:'d MMMM yyyy' }}.</strong>
                            Meta will start charging for replies you send inside the 24-hour window.
                        </ng-container>
                        <ng-container *ngIf="hasStarted">
                            <strong>WhatsApp pricing changed on {{ startsAt | date:'d MMMM yyyy' }}.</strong>
                            Meta now charges for replies you send inside the 24-hour window.
                        </ng-container>
                    </span>
                </div>
                <div class="d-flex align-items-center gap-2">
                    <button type="button" class="btn btn-sm btn-primary" (click)="learnMore()">
                        What changed
                    </button>
                    <button type="button" class="btn btn-sm btn-link text-muted" (click)="dismiss()">
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        /*
         * Deliberately NOT position: fixed.
         *
         * The trial and impersonation banners are both fixed at top: 70px, and a
         * third one there would overlap them whatever the z-index — and would
         * need the .has-subscription-banner offset in vertical.component.scss
         * widened to match. In normal flow this stacks under them for free and
         * pushes the page down by exactly its own height.
         */
        .service-billing-banner {
            background: #fff;
            border: 1px solid rgba(85, 110, 230, .25);
            border-left: 3px solid #556ee6;
            border-radius: 4px;
            margin-bottom: 1rem;
        }
    `]
})
export class ServiceBillingBannerComponent implements OnInit {

    visible = false;
    readonly startsAt = new Date(SERVICE_BILLING_START_MS);

    private readonly billingInfo = inject(ServiceBillingInfoService);

    /** Whether the cutover has already passed. */
    get hasStarted(): boolean {
        return Date.now() >= SERVICE_BILLING_START_MS;
    }

    ngOnInit(): void {
        // Retire on its own once the change is old news.
        if (Date.now() > SERVICE_BILLING_START_MS + GRACE_MS) return;
        this.visible = !this.wasDismissed();
    }

    /** Open the shared explainer. */
    learnMore(): void {
        this.billingInfo.show();
    }

    /** Hide it and remember that. */
    dismiss(): void {
        this.visible = false;
        try {
            localStorage.setItem(DISMISS_KEY, '1');
        } catch {
            // Safari private mode throws on write. Losing the preference is
            // preferable to breaking the page over a dismissed banner.
        }
    }

    /** Whether this browser has already dismissed the notice. */
    private wasDismissed(): boolean {
        try {
            return localStorage.getItem(DISMISS_KEY) === '1';
        } catch {
            return false;
        }
    }
}
